/**
 * PaymentsSettingsPage — Настройки интеграции с ЮKassa
 *
 * Поля: Live ShopID + Secret, Test ShopID + Secret, Switch test/live режим.
 * Действия: сохранить, проверить подключение.
 * Безопасность: secret keys возвращаются с бэкенда только в маске,
 * для замены — пользователь вводит новое значение.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Title, Text, Stack, Box, Group, Button, Loader, Center,
  Paper, TextInput, PasswordInput, Switch, Alert, Badge,
  CopyButton, ActionIcon, Tooltip, Divider,
} from '@mantine/core';
import {
  IconCreditCard, IconCheck, IconCopy, IconAlertCircle,
  IconLink, IconShieldLock, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import tokens from '../theme/designTokens';

const PaymentsSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Поля формы (отдельно от settings, т.к. secrets вводятся в открытом виде)
  const [liveShopId, setLiveShopId] = useState('');
  const [liveSecretKey, setLiveSecretKey] = useState('');
  const [testShopId, setTestShopId] = useState('');
  const [testSecretKey, setTestSecretKey] = useState('');
  const [testModeEnabled, setTestModeEnabled] = useState(true);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/payments/settings', { credentials: 'include' });
      if (!res.ok) throw new Error('Не удалось загрузить настройки');
      const data = await res.json();
      setSettings(data);
      setLiveShopId(data.live_shop_id || '');
      setTestShopId(data.test_shop_id || '');
      setTestModeEnabled(data.test_mode_enabled);
      // Secrets — пустые поля, реальные значения не приходят
      setLiveSecretKey('');
      setTestSecretKey('');
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Отправляем только то что заполнено или изменилось
      // Secret отправляем только если поле непустое (значит юзер ввёл новое)
      const payload = {
        live_shop_id: liveShopId,
        test_shop_id: testShopId,
        test_mode_enabled: testModeEnabled,
      };
      if (liveSecretKey) payload.live_secret_key = liveSecretKey;
      if (testSecretKey) payload.test_secret_key = testSecretKey;

      const res = await fetch('/api/payments/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Ошибка сохранения');
      }
      const data = await res.json();
      setSettings(data);
      // Очищаем поля секретов после сохранения
      setLiveSecretKey('');
      setTestSecretKey('');
      notifications.show({
        title: 'Сохранено',
        message: 'Настройки платежей обновлены',
        color: 'green',
      });
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (mode = 'active') => {
    setTesting(true);
    try {
      const res = await fetch('/api/payments/settings/test-connection', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      notifications.show({
        title: data.success ? 'Подключение успешно' : 'Ошибка подключения',
        message: data.message,
        color: data.success ? 'green' : 'red',
        autoClose: 6000,
      });
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box px="md" py="md">
        <Center py="xl"><Loader size="md" /></Center>
      </Box>
    );
  }

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Платежи
            </Title>
            <Text c="dimmed" size="sm">
              Настройка интеграции с ЮKassa для приёма платежей от родителей
            </Text>
          </Box>
          <Badge
            size="lg"
            variant="filled"
            color={settings?.test_mode_enabled ? 'orange' : 'green'}
            radius="xl"
            leftSection={<IconCreditCard size={14} />}
          >
            {settings?.test_mode_enabled ? 'Тестовый режим' : 'Боевой режим'}
          </Badge>
        </Group>

        {/* Инфо-блок */}
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="blue"
          variant="light"
          title="Важно перед началом"
          radius={16}
        >
          <Stack gap={4}>
            <Text size="sm">
              1. В <Text span fw={600}>ЛК ЮKassa</Text> → Настройки → Онлайн-касса должен быть включён режим
              «<Text span fw={600}>Не связывать платёж и чек</Text>» — это нужно для пробития чеков зачёта при посещениях.
            </Text>
            <Text size="sm">
              2. В разделе HTTP-уведомлений ЮKassa должен быть указан URL для webhook
              (см. ниже) и выбраны события: <Text span fw={600}>payment.succeeded</Text>, <Text span fw={600}>payment.canceled</Text>, <Text span fw={600}>refund.succeeded</Text>.
            </Text>
          </Stack>
        </Alert>

        {/* Webhook URL */}
        <Paper p="md" withBorder radius={16}>
          <Stack gap="xs">
            <Group gap="xs">
              <IconLink size={16} color={tokens.colors.brand.primary} />
              <Text fw={600} size="sm">URL для HTTP-уведомлений ЮKassa</Text>
            </Group>
            <Group gap="xs">
              <Text
                size="sm"
                style={{
                  fontFamily: 'monospace',
                  backgroundColor: tokens.colors.neutral[1],
                  padding: '6px 12px',
                  borderRadius: 6,
                  flex: 1,
                }}
              >
                {settings?.webhook_url}
              </Text>
              <CopyButton value={settings?.webhook_url || ''}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Скопировано' : 'Скопировать'}>
                    <ActionIcon variant="subtle" onClick={copy}>
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </Stack>
        </Paper>

        {/* Тестовый режим */}
        <Paper p="lg" withBorder radius={16}>
          <Stack gap="md">
            <Group justify="space-between">
              <Group gap="xs">
                <Badge color="orange" variant="filled" radius="xl">TEST</Badge>
                <Text fw={600}>Тестовый магазин</Text>
                {settings?.test_configured && (
                  <Badge color="green" variant="light" radius="xl" size="sm">
                    Настроен
                  </Badge>
                )}
              </Group>
              {settings?.test_configured && (
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => handleTestConnection('test')}
                  loading={testing}
                >
                  Проверить подключение
                </Button>
              )}
            </Group>

            <Group grow>
              <TextInput
                label="ShopID"
                placeholder="1324406"
                value={testShopId}
                onChange={(e) => setTestShopId(e.target.value)}
                radius="xl"
              />
              <PasswordInput
                label="Secret Key"
                placeholder={settings?.test_secret_key_masked || 'test_...'}
                value={testSecretKey}
                onChange={(e) => setTestSecretKey(e.target.value)}
                description={settings?.test_secret_key_masked ? `Текущий: ${settings.test_secret_key_masked}. Введите новый, чтобы заменить.` : 'Из ЛК ЮKassa → Настройки → API ключи'}
                radius="xl"
              />
            </Group>
          </Stack>
        </Paper>

        {/* Боевой режим */}
        <Paper p="lg" withBorder radius={16}>
          <Stack gap="md">
            <Group justify="space-between">
              <Group gap="xs">
                <Badge color="green" variant="filled" radius="xl">LIVE</Badge>
                <Text fw={600}>Боевой магазин</Text>
                {settings?.live_configured && (
                  <Badge color="green" variant="light" radius="xl" size="sm">
                    Настроен
                  </Badge>
                )}
              </Group>
              {settings?.live_configured && (
                <Button
                  size="xs"
                  variant="subtle"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => handleTestConnection('live')}
                  loading={testing}
                >
                  Проверить подключение
                </Button>
              )}
            </Group>

            <Group grow>
              <TextInput
                label="ShopID"
                placeholder="100000"
                value={liveShopId}
                onChange={(e) => setLiveShopId(e.target.value)}
                radius="xl"
              />
              <PasswordInput
                label="Secret Key"
                placeholder={settings?.live_secret_key_masked || 'live_...'}
                value={liveSecretKey}
                onChange={(e) => setLiveSecretKey(e.target.value)}
                description={settings?.live_secret_key_masked ? `Текущий: ${settings.live_secret_key_masked}. Введите новый, чтобы заменить.` : 'Из ЛК ЮKassa → Настройки → API ключи'}
                radius="xl"
              />
            </Group>
          </Stack>
        </Paper>

        {/* Активный режим */}
        <Paper p="lg" withBorder radius={16}>
          <Group justify="space-between">
            <Group gap="md">
              <IconShieldLock size={24} color={tokens.colors.brand.primary} />
              <Box>
                <Text fw={600}>Активный режим работы</Text>
                <Text size="xs" c="dimmed">
                  Определяет, какой набор ключей будет использоваться для приёма платежей
                </Text>
              </Box>
            </Group>
            <Switch
              size="lg"
              checked={testModeEnabled}
              onChange={(e) => setTestModeEnabled(e.currentTarget.checked)}
              onLabel="TEST"
              offLabel="LIVE"
              color="orange"
            />
          </Group>
        </Paper>

        <Divider />

        {/* Кнопка сохранения */}
        <Group justify="flex-end">
          <Button
            size="md"
            onClick={handleSave}
            loading={saving}
            leftSection={<IconCheck size={16} />}
          >
            Сохранить настройки
          </Button>
        </Group>
      </Stack>
    </Box>
  );
};

export default PaymentsSettingsPage;
